import { LoadingState, Alert } from '../ui';
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import SidebarNavbar from "../components/SidebarNavbar";
import BottomNavbar from "../components/BottomNavbar";
import { Button as FcButton, Card as FcCard, LinkButton as FcLinkButton } from '../ui';

export default function CompetitionJoinLanding() {
  const { joinKey } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [competition, setCompetition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);

  useEffect(() => {
    checkSession();
    fetchCompetitionInfo();
  }, [joinKey]);

  const checkSession = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setSession(session);
  };

  const fetchCompetitionInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch public competition info using registration_key
      const { data, error } = await supabase
        .from("competitions")
        .select("id, name, type, status, season, league, registration_status, player_count, start_date")
        .eq("registration_key", joinKey)
        .eq("visibility", "private")
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          setError("Competition not found. This invite link may be invalid or expired.");
        } else {
          throw error;
        }
        return;
      }

      if (data.status === "completed" || data.status === "cancelled") {
        setError("This competition has ended and is no longer accepting new members.");
        return;
      }

      // Get entry count
      const { count } = await supabase
        .from("user_selections")
        .select("*", { count: "exact", head: true })
        .eq("competition_id", data.id);

      setCompetition({ ...data, entry_count: count || 0 });
    } catch (err) {
      setError("Failed to load competition details. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = () => {
    if (session) {
      // Already logged in - go directly to dashboard with join parameter
      navigate(`/dashboard?join=${joinKey}`);
    } else {
      // Store join intent and redirect to signup (using localStorage to persist across email verification)
      localStorage.setItem("pendingJoin", joinKey);
      
      // Check if there's a referral code in the URL (from whoever shared the link)
      const refCode = searchParams.get('ref');
      if (refCode) {
        localStorage.setItem("pendingReferral", refCode);
        navigate(`/signup?ref=${refCode}`);
      } else {
        navigate("/signup");
      }
    }
  };

  const handleLogin = () => {
    localStorage.setItem("pendingJoin", joinKey);
    
    // Check if there's a referral code in the URL (from whoever shared the link)
    const refCode = searchParams.get('ref');
    if (refCode) {
      localStorage.setItem("pendingReferral", refCode);
    }
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-canvas">
        <LoadingState label="Loading competition details..." />
      </div>
    );
  }

  if (error) {
    return (
      <>
        <SidebarNavbar session={session} />
        <div role="main" id="fc-main-content" className="min-h-screen flex items-center justify-center bg-surface-canvas p-4 md:ml-64 pb-20 fc-workspace">
          <FcCard as="div" padding="standard" className="bg-surface-card p-8 rounded-lg shadow-lg max-w-md w-full text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-ui-green mb-4">Oops!</h1>
            <Alert tone="danger">{error}</Alert>
            <FcLinkButton variant="primary"
              to="/competitions"
              className="inline-block"
            >
              Browse Competitions
            </FcLinkButton>
          </FcCard>
        </div>
        <BottomNavbar session={session} />
      </>
    );
  }

  if (!competition) {
    return null;
  }

  const getTypeLabel = (type) => {
    const labels = {
      season: "Season Long",
      knockout: "Knockout",
      scout: "Scout",
    };
    return labels[type] || type;
  };

  return (
    <>
      <SidebarNavbar session={session} />
      <div role="main" id="fc-main-content" className="min-h-screen flex items-center justify-center bg-surface-canvas p-4 md:ml-64 pb-20 fc-workspace">
        <FcCard as="div" padding="standard" className="bg-surface-card p-8 rounded-lg shadow-lg max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-5xl mb-4">🏆</div>
          <h1 className="text-2xl font-bold text-ui-green mb-2">
            You've Been Invited!
          </h1>
          <p className="text-content-secondary">Join this private competition</p>
        </div>

        {/* Competition Details */}
        <div className="bg-surface-canvas rounded-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-action-primary mb-4 text-center">
            {competition.name}
          </h2>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-content-secondary font-medium">Type:</span>
              <span className="text-ui-green font-semibold">{getTypeLabel(competition.type)}</span>
            </div>
            
            {competition.league && (
              <div className="flex justify-between">
                <span className="text-content-secondary font-medium">League:</span>
                <span className="text-ui-green font-semibold">{competition.league}</span>
              </div>
            )}
            
            {competition.season && (
              <div className="flex justify-between">
                <span className="text-content-secondary font-medium">Season:</span>
                <span className="text-ui-green font-semibold">{competition.season}</span>
              </div>
            )}
            
            <div className="flex justify-between">
              <span className="text-content-secondary font-medium">Participants:</span>
              <span className="text-ui-green font-semibold">{competition.entry_count}</span>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        {session ? (
          <FcButton variant="primary" type="submit"
            onClick={handleJoin}
            className="w-full mb-3"
          >
            Join Competition
          </FcButton>
        ) : (
          <>
            <FcButton variant="primary" type="submit"
              onClick={handleJoin}
              className="w-full mb-3"
            >
              Sign Up & Join
            </FcButton>
            <FcButton variant="secondary" type="submit"
              onClick={handleLogin}
              className="w-full border-2"
            >
              Already have an account? Login
            </FcButton>
          </>
        )}

        {/* Footer Link */}
        <div className="mt-6 text-center">
          <Link to="/competitions" className="text-action-primary hover:underline text-sm">
            Browse all public competitions
          </Link>
        </div>
        </FcCard>
      </div>
      <BottomNavbar session={session} />
    </>
  );
}
